import { useEffect, useState } from "react";
import "./App.scss";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import Header from "@/components/common/header/header";
import Footer from "@/components/common/footer/footer";
import TermsOfService from "@/pages/terms-of-service/terms-of-service";
import PrivacyPolicy from "@/pages/privacy-policy/privacy-policy";
import Leaderboard from "@/pages/leaderboard/leaderboard";
import { Toaster, toast } from "react-hot-toast";
import config from "@/config";
import { Auth0Provider } from "@auth0/auth0-react";
import { isEmpty, isNull } from "lodash";
import { findOrCreateUser, updateUserLastVisit } from "@/services/UserService";
import {
    getAllPendingGuessesForRegistration,
    clearGuestPuzzleLocalStorageAfterImport,
} from "@/helpers/puzzlePendingGuessesStorage";
import {
    PUZZLE_GUEST_SCORES_MIGRATING_KEY,
    PUZZLE_OPEN_SCOREBOARD_KEY,
    PUZZLE_REGISTER_LOCATION_KEY,
} from "@/constants/puzzle-storage.constants";
import { MIXPANEL_REGISTER_LOCATION_GOOGLE_ONE_TAP_MODAL } from "@/constants/mixpanel.constants";
import { createReferredUser } from "@/services/ReferralService";
import { useUser } from "@/context/UserContext";
import useLocalStorage from "@/hooks/useLocalStorage";
import { getAreaById } from "@/services/AreaService";
import { checkVersion } from "version-rocket";
import { TOAST_MESSAGES } from "@/lang/toast-messages.lang";
import RootModals from "@/components/modals/root-modals/root-modals";
import ChooseNicknameModal from "@/components/modals/choose-nickname-modal/ChooseNicknameModal";
import NotFound from "@/pages/not-found/not-found";
import MetaPixel from "@/components/common/meta-pixel";
import ReferralWrapper from "@/components/common/referral-wrapper";
import useGAEvent from "@/hooks/useGAEvent";
import { AppContextProvider } from "@/AppContextProvider";
import { logger } from "@/services/LoggerService";
import CreditsPage from "@/pages/credits";
import PlusMinus from "@/pages/PlusMinus";
import ShortUrlRedirect from "@/pages/LiveVariant/ShortUrlRedirect";
import Puzzle from "@/pages/LiveVariant/components/Puzzle";
import PuzzleFeed from "@/pages/LiveVariant/components/PuzzleFeed/PuzzleFeed";
import PuzzleLegacyRedirect from "@/components/common/puzzle-legacy-redirect/puzzle-legacy-redirect";
import { PLUS_MINUS_VERSIONS } from "@/constants/plus-minus-versions";
import AnimationTest from "@/components/animations/AnimationTest";
import MixpanelUserTracker from "@/components/MixpanelUserTracker";
import AuthTokenBridge from "@/components/AuthTokenBridge";
import PendingLikeReplay from "@/components/common/pending-like-replay/pending-like-replay";
import SetPuzzleAdmin from "@/pages/admin/set-puzzle";
import AdminPropertyEditor from "@/pages/admin/property-editor";
import { FormattedAuthUser } from "./types/user.types";
import { cstMoment } from "@common/utils/date.utils";
import PageHead from "./components/common/PageHead";
import Sitemap from "./pages/sitemap";
import About from "./pages/about";
import Contact from "./pages/contact";
import Profile from "./pages/profile/Profile";
import StorageDebugPanel from "./components/debug/StorageDebugPanel";
/**
 * @deprecated Classic entry points retired; components kept on disk.
 * import DailyProperties from "@/pages/daily-properties/daily-properties";
 * import StartupScreen from "@/pages/startup-screen/startup-screen";
 * import ProtectedRoute from "@/components/common/protected-route/protected-route";
 * import HasCurrentAreaGuard from "@/guards/has-current-area-guard";
 * import IsRegisteredUser from "@/guards/is-registered-user-guard";
 * import HasSelectedLocation from "@/guards/has-selected-location-guard";
 * import ChallengeMode from "@/pages/ChallengeMode";
 * import ChallengeRedirect from "@/pages/ChallengeRedirect";
 * import ChooseGame from "@/pages/ChooseGame";
 * import FAQ from "./pages/faq/faq";
 */
const AUTH0_AUDIENCE = "https://dev-kb71sgmlh0p32db1.us.auth0.com/api/v2/";

const appVersion = require("../package.json").version;

const pollingTime = 30000; // every 30 seconds

/**
 * Icons for toast messages.
 *
 * @type {Object}
 * @property {JSX.Element} success - Success icon for toast messages.
 */
const toastIcons = {
    success: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
                d="M12 1.875C6.417 1.875 1.875 6.417 1.875 12C1.875 17.583 6.417 22.125 12 22.125C17.583 22.125 22.125 17.583 22.125 12C22.125 6.417 17.583 1.875 12 1.875ZM12 19.875C7.658 19.875 4.125 16.342 4.125 12C4.125 7.658 7.658 4.125 12 4.125C16.342 4.125 19.875 7.658 19.875 12C19.875 16.342 16.342 19.875 12 19.875Z"
                fill="#0EBE1F"
            />
            <path
                d="M16.2051 8.20508L11.0001 13.4091L8.79508 11.2051L7.20508 12.7951L11.0001 16.5911L17.7951 9.79508L16.2051 8.20508Z"
                fill="#0EBE1F"
            />
        </svg>
    ),
};

function App() {
    const queryClient = useQueryClient();
    const [isUserRegistered, setIsUserRegistered] = useState(false);
    const [showFooterSpacer, setShowFooterSpacer] = useState(false);
    const [value, , clearValue] = useLocalStorage("guest_gameplay", null); // eslint-disable-line no-unused-vars
    const [visitorStatus, setVisitorStatus] = useLocalStorage("visitor-status", null);
    const [lastVisit, setLastVisit] = useLocalStorage("last-visit", null);
    const [showChooseNickname, setShowChooseNickname] = useState(false);
    const { userState, setUserState, setInvalidLogin, fetchUserData } = useUser();
    const { sendEvent } = useGAEvent();
    const isPuzzle =
        window.location.pathname === "/" ||
        window.location.pathname === "/puzzle" ||
        window.location.pathname.includes("/puzzle/");

    useEffect(() => {
        let rafId: number | null = null;

        const updateSpacerVisibility = () => {
            const isPuzzleRoute =
                window.location.pathname === "/" ||
                window.location.pathname === "/puzzle" ||
                window.location.pathname.includes("/puzzle/");
            const rootStyle = document.documentElement.style;
            if (!isPuzzleRoute) {
                setShowFooterSpacer(false);
                rootStyle.setProperty("--puzzle-guess-bar-height", "0px");
                return;
            }

            const guessBar = document.getElementById("puzzle-guess-input-bar");
            setShowFooterSpacer(Boolean(guessBar));
            if (guessBar) {
                const measuredHeight = Math.ceil(guessBar.getBoundingClientRect().height);
                rootStyle.setProperty("--puzzle-guess-bar-height", `${measuredHeight}px`);
            } else {
                rootStyle.setProperty("--puzzle-guess-bar-height", "0px");
            }
        };

        const scheduleVisibilityCheck = () => {
            if (rafId !== null) return;
            rafId = window.requestAnimationFrame(() => {
                rafId = null;
                updateSpacerVisibility();
            });
        };

        updateSpacerVisibility();
        const observer = new MutationObserver(scheduleVisibilityCheck);
        const observerTarget =
            document.querySelector(".app-content") ?? document.getElementById("root") ?? document.body;
        observer.observe(observerTarget, { childList: true, subtree: true });
        window.addEventListener("resize", scheduleVisibilityCheck);

        return () => {
            if (rafId !== null) {
                window.cancelAnimationFrame(rafId);
            }
            window.removeEventListener("resize", scheduleVisibilityCheck);
            observer.disconnect();
        };
    }, []);

    /**
     * Callback function for handling the redirect after a user logs in.
     * Any errors in this custom callback will result in an infinite redirect after login
     *
     * @param {Object} appState - The application state returned from the authentication process.
     *
     * @param {Object} user - The user object returned from the authentication process.
     */
    const onRedirectCallback = async (appState: any, user: any) => {
        const returnTo = appState?.returnTo || window.location.pathname;
        // Clears the history state by removing the login code
        window.history.replaceState({}, document.title, returnTo);
        // Notify React Router about the URL change (replaceState alone doesn't trigger popstate)
        window.dispatchEvent(new PopStateEvent("popstate", { state: {} }));

        // Determine the app version for the user (default to puzzle if not specified)
        const version = !isEmpty(appState.version) ? appState.version : "puzzle";

        // Ensure nickname is always a non-empty string: Auth0 can omit it or send null/undefined,
        // which causes server validation errors and registration/navigation failures (findOrCreateUser/_generateReferralCode).
        const nickname =
            (typeof user.nickname === "string" && user.nickname.trim()) ||
            (user.email && String(user.email).split("@")[0]) ||
            "User";
        const formattedUserData: FormattedAuthUser = {
            email: user.email,
            emailVerified: user.email_verified,
            name: user.name,
            nickname,
            picture: user.picture,
            sub: user.sub,
            updatedAt: user.updated_at,
            givenName: user.given_name,
            familyName: user.family_name,
            version,
            onboardingTimestamp: cstMoment().toISOString(),
            currentAreaId: isEmpty(userState.currentAreaId) ? null : userState.currentAreaId,
        };

        const currentArea = isNull(formattedUserData.currentAreaId)
            ? null
            : await getAreaById(userState?.currentAreaId);

        const source = appState?.version === "classic" ? "Classic" : "Puzzle";
        let location = appState?.location || MIXPANEL_REGISTER_LOCATION_GOOGLE_ONE_TAP_MODAL;
        try {
            const registerLocationOverride = sessionStorage.getItem(PUZZLE_REGISTER_LOCATION_KEY);
            if (registerLocationOverride) {
                location = registerLocationOverride;
                sessionStorage.removeItem(PUZZLE_REGISTER_LOCATION_KEY);
            }
        } catch {
            /* ignore */
        }
        const referringUrl = document.referrer || window.location.origin;

        const pendingGuesses = getAllPendingGuessesForRegistration();
        if (pendingGuesses.length > 0) {
            try {
                sessionStorage.setItem(PUZZLE_GUEST_SCORES_MIGRATING_KEY, "true");
                if (
                    appState?.version === "puzzle" ||
                    String(returnTo).includes("#scoreboard") ||
                    String(returnTo).includes("view=scoreboard")
                ) {
                    sessionStorage.setItem(PUZZLE_OPEN_SCOREBOARD_KEY, "true");
                }
            } catch {
                /* ignore */
            }
        }

        findOrCreateUser(formattedUserData, value ?? null, {
            referringUrl,
            source,
            location,
        }, pendingGuesses.length > 0 ? pendingGuesses : undefined)
            .then((response) => {
                const { data } = response;

                if (data) {
                    if (pendingGuesses.length > 0) {
                        clearGuestPuzzleLocalStorageAfterImport();
                        void queryClient.invalidateQueries({ queryKey: ["puzzle_daily_property_guess"], exact: false });
                        void queryClient.invalidateQueries({
                            queryKey: ["puzzle-scoreboard-current-user"],
                            exact: false,
                        });
                        void queryClient.invalidateQueries({
                            queryKey: ["playable-property-comments"],
                            exact: false,
                        });
                        void queryClient.invalidateQueries({ queryKey: ["property-puzzle-stats"], exact: false });
                        void queryClient.invalidateQueries({ queryKey: ["puzzle-feed"], exact: false });
                        toast.success("Scores saved.");
                        try {
                            sessionStorage.removeItem(PUZZLE_GUEST_SCORES_MIGRATING_KEY);
                        } catch {
                            /* ignore */
                        }
                    }
                    setUserState(data.user, data.isUserCreated);

                    // It sets here the user notif settings
                    fetchUserData(data.user.id);

                    // If referral code exists, remove after user creation

                    if (data.isUserCreated) {
                        let referralCode = localStorage.getItem("referral_code");

                        // Send a GA event if there is an event object in the appState
                        if (!isEmpty(appState?.event)) {
                            const { referral_code, final_score } = appState?.event;

                            referralCode = referral_code;

                            sendEvent(appState?.event?.name, {
                                isAuthenticated: false,
                                referralCode: referral_code,
                                finalScore: final_score,
                                userRank: 0,
                            });
                        }

                        sendEvent("register", {
                            userArea: currentArea?.data?.name ?? "None",
                            isAuthenticated: false,
                            referralCode: referralCode,
                            guestChallengeRedirect: userState?.guest_challenge_redirect,
                            challengeRegisterButton: userState?.challenge_register_button,
                            guestFinalScoreModalVersion: userState?.guest_final_score_modal_version,
                        } as any);

                        // Register Mixpanel event is server-only (see users.service findOrCreateUser).
                        // sendMixpanelEvent("Register", {
                        //     isAuthenticated: false,
                        //     referringUrl,
                        //     source,
                        //     location,
                        //     user_id: data.user?.id,
                        //     nickname: data.user?.nickname,
                        //     email: data.user?.emailAddress,
                        // });

                        // If referral code exists, create referred user instance and remove code after user creation
                        if (referralCode && !isEmpty(referralCode) && !isEmpty(data.user?.emailAddress)) {
                            createReferredUser({
                                referralId: referralCode,
                                userEmail: data.user.emailAddress,
                            });
                            localStorage.removeItem("referral_code");
                        }

                        if (typeof (window as any).rdt === "function") {
                            (window as any).rdt("track", "SignUp");
                        }

                        // For Meta Pixel tagging
                        setIsUserRegistered(true);

                        // Mark this device so we show the nickname modal/drawer once for new users
                        localStorage.setItem("showSetNicknameModal", "true");

                        toast.success(`You're in! Welcome to PriceMe.`);
                    }

                    if (!isEmpty(data.user) && !data.isUserCreated) {
                        toast.success(`You're in! Welcome to PriceMe.`);
                    }

                    // Clear the guest gameplay data from the local storage since we already saved it to the database
                    if (value) {
                        clearValue();
                    }
                } else {
                    toast.error(`There was an error with retrieving the user from the server.`);
                }
            })
            .catch((error) => {
                setInvalidLogin(true);
                try {
                    sessionStorage.removeItem(PUZZLE_GUEST_SCORES_MIGRATING_KEY);
                } catch {
                    /* ignore */
                }

                // Extract error message - NestJS typically returns errors in response.data.message
                // For validation errors, it might be an array, so we handle both cases
                let errorMessage: string;

                if (error.response?.data?.message) {
                    const message = error.response.data.message;
                    // Handle array of validation errors
                    if (Array.isArray(message)) {
                        errorMessage = message
                            .map((m: any) => {
                                // Handle nested validation error objects
                                if (typeof m === "object" && m.message) {
                                    return m.message;
                                }
                                return String(m);
                            })
                            .join(", ");
                    } else {
                        errorMessage = String(message);
                    }
                } else if (error.response?.data?.error) {
                    errorMessage = String(error.response.data.error);
                } else if (error.message) {
                    errorMessage = error.message;
                } else {
                    errorMessage = "Unable to log in. Please try again.";
                }

                toast.error(errorMessage);
                logger.error("Error during user login/registration", error, {
                    userEmail: user.email,
                    userName: user.name,
                    authError: true,
                    status: error.response?.status,
                    responseData: error.response?.data,
                });
            });
    };

    // Initialize the check version service worker for polling the version (if not in development)
    if (process.env.REACT_APP_STAGE !== "development") {
        checkVersion(
            {
                localPackageVersion: appVersion,
                originVersionFileUrl: `${window.location.origin}/version.json`,
                pollingTime,
            },
            {
                imageUrl: undefined,
                title: TOAST_MESSAGES.NEW_VERSION.TITLE,
                description: TOAST_MESSAGES.NEW_VERSION.DESCRIPTION,
                cancelButtonText: "Later",
                cancelMode: "ignore-current-window",
            },
        );
    }

    useEffect(() => {
        async function lastVisitUpdate() {
            const currentTime = cstMoment();

            if (!lastVisit) {
                setVisitorStatus("new" as any);
                setLastVisit(currentTime.toISOString() as any);
            } else {
                const lastVisitTime = cstMoment(lastVisit);

                if (!userState?.id) {
                    return;
                }

                try {
                    await updateUserLastVisit(userState.id, currentTime.toISOString());
                } catch (error) {
                    logger.error("Failed to update user last visit", error, {
                        userId: userState.id,
                    });
                }

                const hoursSinceLastVisit = currentTime.diff(lastVisitTime, "hours");

                if (hoursSinceLastVisit >= 24 && visitorStatus !== "repeat") {
                    setVisitorStatus("repeat" as any);
                    setLastVisit(currentTime.toISOString() as any);
                }
            }
        }
        lastVisitUpdate();
    }, [lastVisit, visitorStatus, setVisitorStatus, setLastVisit, userState?.id]);

    // Show choose-nickname modal/drawer when:
    // 1) New user (first login) OR 2) lastVisitedAt < launch date (2026-02-25);
    // then only once per day and once per login (so we don't show again same day or same session).
    useEffect(() => {
        if (!userState?.id) return;

        const launchDate = new Date("2026-02-25T00:00:00Z");
        const rawLastVisited: any =
            (userState as any)?.lastVisitedAt ?? (userState as any)?.lastVisited ?? null;
        const lastVisitedAt = rawLastVisited ? new Date(rawLastVisited) : null;

        const isNewUser = localStorage.getItem("showSetNicknameModal") === "true";
        const isLegacyUser =
            lastVisitedAt !== null && lastVisitedAt.getTime() < launchDate.getTime();
        if (!isNewUser && !isLegacyUser) return;

        const today = new Date().toISOString().slice(0, 10);
        const shownToday = localStorage.getItem("nicknameModalShownDate") === today;
        const shownThisSession = sessionStorage.getItem("nicknameModalShownThisSession") === "true";
        if (shownToday || shownThisSession) return;

        setShowChooseNickname(true);
        // We intentionally only depend on stable user identity.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userState?.id]);

    const handleCloseChooseNickname = () => {
        setShowChooseNickname(false);
        localStorage.setItem("showSetNicknameModal", "false");
        const today = new Date().toISOString().slice(0, 10);
        localStorage.setItem("nicknameModalShownDate", today);
        sessionStorage.setItem("nicknameModalShownThisSession", "true");
    };

    return (
        <Auth0Provider
            domain={config.AUTH0.domain}
            clientId={config.AUTH0.client_id}
            authorizationParams={{
                redirect_uri: window.location.origin,
                audience: AUTH0_AUDIENCE,
            }}
            useRefreshTokens={true}
            cacheLocation="localstorage"
            onRedirectCallback={onRedirectCallback}
        >
            <AuthTokenBridge />
            <MixpanelUserTracker />
            <PendingLikeReplay />
            <div className="app-container">
                <BrowserRouter>
                    <MetaPixel isUserRegistered={isUserRegistered} />
                    <ReferralWrapper />
                    <AppContextProvider>
                        <Header />
                        <div className="app-content">
                            <Toaster
                                reverseOrder
                                position="top-center"
                                containerStyle={{
                                    top: isPuzzle ? "15svh" : "11svh",
                                    zIndex: 1500,
                                    inset: `${isPuzzle ? "15svh" : "11svh"} 16px 16px`,
                                }}
                                toastOptions={{
                                    className: "custom-toast",
                                    duration: 5000,
                                    success: {
                                        className: "custom-toast custom-success-toast",
                                        icon: toastIcons.success,
                                    },
                                    error: {
                                        className: "custom-toast custom-error-toast",
                                    },
                                }}
                            />
                            <RootModals />
                            <ChooseNicknameModal
                                show={showChooseNickname}
                                onClose={handleCloseChooseNickname}
                                nickname={userState?.nickname}
                                showScoreLeaderboard={userState?.showScoreLeaderboard ?? true}
                            />
                            <StorageDebugPanel />
                            <Routes>
                                <Route path="/" element={<PuzzleFeed />} />
                                <Route path="/puzzle" element={<PuzzleLegacyRedirect />} />
                                <Route path="/puzzle/:date/:propertyDetails" element={<Puzzle version="v5" />} />
                                <Route path="/puzzle/:shortId" element={<ShortUrlRedirect />} />
                                <Route path="/classic/faq" element={<Navigate to="/" replace />} />
                                {/* was: <Route path="/classic/faq" element={<Navigate to="/faq" replace />} /> */}

                                {/*
                                <Route
                                    path="/"
                                    element={
                                        <>
                                            <PageHead
                                                title="PriceMe | Choose Game"
                                                description="Try one of our real estate games to see how well you know the market"
                                                canonicalUrl={`${config.BASE_URL}/`}
                                            />
                                            <ChooseGame />
                                        </>
                                    }
                                />

                                <Route
                                    path="/classic"
                                    element={
                                        <ProtectedRoute
                                            navigateData={{
                                                replace: false,
                                                to: "/classic",
                                                state: { failed_guard: "true" },
                                            }}
                                            guards={[IsRegisteredUser]}
                                        >
                                            <>
                                                <PageHead
                                                    title="PriceMe | Classic"
                                                    description="Guess the price of 3 homes that just sold near you. The closer your guess, the more points you get"
                                                    canonicalUrl={`${config.BASE_URL}/classic`}
                                                />
                                                <StartupScreen />
                                            </>
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/daily-properties"
                                    element={
                                        <ProtectedRoute
                                            navigateData={{
                                                replace: false,
                                                to: "/classic",
                                                state: { failed_guard: "true" },
                                            }}
                                            guards={[HasSelectedLocation, HasCurrentAreaGuard]}
                                        >
                                            <>
                                                <PageHead
                                                    title="PriceMe | Classic - Daily Properties"
                                                    description="Guess the price of 3 homes that just sold near you. The closer your guess, the more points you get"
                                                    canonicalUrl={`${config.BASE_URL}/daily-properties`}
                                                />
                                                <DailyProperties />
                                            </>
                                        </ProtectedRoute>
                                    }
                                />

                                <Route path="/challenge/:challenge_id" element={<ChallengeRedirect />} />
                                <Route path="/classic/challenge" element={<ChallengeMode />} />

                                <Route
                                    path="/classic/faq"
                                    element={
                                        <>
                                            <PageHead
                                                title="PriceMe | FAQ - Classic Mode"
                                                description="Frequently asked questions about PriceMe Classic game mode"
                                                canonicalUrl={`${config.BASE_URL}/classic/faq`}
                                            />
                                            <FAQ />
                                        </>
                                    }
                                />

                                <Route path="/puzzle" element={<PuzzleFeed />} />

                                <Route path="/classic" element={<Navigate to="/" replace />} />
                                <Route path="/classic/challenge" element={<Navigate to="/" replace />} />
                                <Route path="/daily-properties" element={<Navigate to="/" replace />} />
                                <Route path="/challenge/:challenge_id" element={<Navigate to="/" replace />} />
                                <Route path="/social" element={<Navigate to="/" replace />} />
                                <Route path="/social/*" element={<Navigate to="/" replace />} />
                                */}

                                <Route path="/test/animations" element={<AnimationTest />} />

                                <Route
                                    path="/admin/set-puzzle"
                                    element={
                                        <>
                                            <PageHead title="PriceMe | Admin - Set Puzzle" titleOnly noIndex />
                                            <SetPuzzleAdmin />
                                        </>
                                    }
                                />
                                <Route
                                    path="/admin/property/:slug"
                                    element={
                                        <>
                                            <PageHead title="PriceMe | Admin - Property Editor" titleOnly noIndex />
                                            <AdminPropertyEditor />
                                        </>
                                    }
                                />
                                <Route
                                    path="/admin/property/add"
                                    element={
                                        <>
                                            <PageHead title="PriceMe | Admin - Add Property" titleOnly noIndex />
                                            <AdminPropertyEditor />
                                        </>
                                    }
                                />

                                {Object.keys(PLUS_MINUS_VERSIONS).map((version) => (
                                    <Route
                                        key={version}
                                        path={`/plus-minus/${version}`}
                                        element={
                                            (PLUS_MINUS_VERSIONS as any)[version]?.component ? (
                                                (PLUS_MINUS_VERSIONS as any)[version].component
                                            ) : (
                                                <PlusMinus version={version} />
                                            )
                                        }
                                    />
                                ))}

                                <Route
                                    path="/terms-of-service"
                                    element={
                                        <>
                                            <PageHead
                                                title="PriceMe | Terms of Service"
                                                description="Read the terms of service for using PriceMe"
                                                canonicalUrl={`${config.BASE_URL}/terms-of-service`}
                                            />
                                            <TermsOfService />
                                        </>
                                    }
                                ></Route>
                                <Route
                                    path="/privacy-policy"
                                    element={
                                        <>
                                            <PageHead
                                                title="PriceMe | Privacy Policy"
                                                description="Read the privacy policy for using PriceMe"
                                                canonicalUrl={`${config.BASE_URL}/privacy-policy`}
                                            />
                                            <PrivacyPolicy />
                                        </>
                                    }
                                ></Route>
                                <Route
                                    path="/leaderboard"
                                    element={
                                        <>
                                            <PageHead
                                                title="PriceMe | Leaderboards"
                                                description="See the top players on PriceMe and compete for the highest score"
                                                canonicalUrl={`${config.BASE_URL}/leaderboard`}
                                            />
                                            <Leaderboard />
                                        </>
                                    }
                                ></Route>
                                <Route
                                    path="/credits"
                                    element={
                                        <>
                                            <PageHead
                                                title="PriceMe | Credits"
                                                description="Credits for assets used in PriceMe"
                                                canonicalUrl={`${config.BASE_URL}/credits`}
                                            />
                                            <CreditsPage />
                                        </>
                                    }
                                ></Route>

                                <Route
                                    path="/sitemap"
                                    element={
                                        <>
                                            <PageHead
                                                title="PriceMe | Sitemap"
                                                description="Complete sitemap of all pages on PriceMe"
                                                canonicalUrl={`${config.BASE_URL}/sitemap`}
                                            />
                                            <Sitemap />
                                        </>
                                    }
                                />

                                <Route
                                    path="/about"
                                    element={
                                        <>
                                            <PageHead
                                                title="PriceMe | About Us"
                                                description="Learn about PriceMe - the real estate guessing game that tests your market knowledge"
                                                canonicalUrl={`${config.BASE_URL}/about`}
                                            />
                                            <About />
                                        </>
                                    }
                                />

                                <Route
                                    path="/contact"
                                    element={
                                        <>
                                            <PageHead
                                                title="PriceMe | Contact Us"
                                                description="Get in touch with the PriceMe team for support, feedback, or business inquiries"
                                                canonicalUrl={`${config.BASE_URL}/contact`}
                                            />
                                            <Contact />
                                        </>
                                    }
                                />

                                {/*
                                <Route
                                    path="/faq"
                                    element={
                                        <>
                                            <PageHead
                                                title="PriceMe | FAQ"
                                                description="Frequently asked questions about PriceMe"
                                                canonicalUrl={`${config.BASE_URL}/faq`}
                                            />
                                            <FAQ />
                                        </>
                                    }
                                />
                                */}

                                <Route path="/profile/:nickname" element={<Profile />} />

                                <Route
                                    path="*"
                                    element={
                                        <>
                                            <PageHead
                                                title="PriceMe | 404 Not Found"
                                                description="The page you are looking for does not exist."
                                            />
                                            <NotFound />
                                        </>
                                    }
                                />
                            </Routes>
                        </div>
                    </AppContextProvider>
                </BrowserRouter>
                <Footer />
                {showFooterSpacer && <div className="footer-spacer" />}
            </div>
        </Auth0Provider>
    );
}

export default App;
